import React, { useState, useEffect, useRef } from "react";
import type {
  Task,
  User,
  Tag,
  CustomColumn,
  TaskTemplate as BaseTaskTemplate,
} from "../types/types.ts"; // 元のTaskTemplateをBaseTaskTemplateとしてインポート
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import LabelImportantTwoToneIcon from "@mui/icons-material/LabelImportantTwoTone";
import StyleTwoToneIcon from "@mui/icons-material/StyleTwoTone";
import NewLabelTwoToneIcon from "@mui/icons-material/NewLabelTwoTone";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { MemberSelectionDialog } from "./MemberSelectionDialog.tsx";
import { isoToYmd, ymdToUtcIso, addDaysIso, todayYmd } from "../utils/date.ts";
import {
  PaletteColor,
  PaletteColorHex,
  PaletteColorDarkHex,
} from "../types/PaletteColor.ts";
import { useDarkMode } from "../hooks/useDarkMode.ts";
import PeopleTwoToneIcon from "@mui/icons-material/PeopleTwoTone";
import RecordVoiceOverTwoToneIcon from "@mui/icons-material/RecordVoiceOverTwoTone";
import EventTwoToneIcon from "@mui/icons-material/EventTwoTone";
import StickyNote2TwoToneIcon from "@mui/icons-material/StickyNote2TwoTone";
import PersonAddTwoToneIcon from "@mui/icons-material/PersonAddTwoTone";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import LockIcon from "@mui/icons-material/Lock";
import TaskIcon from "@mui/icons-material/Task";
import ListAltIcon from "@mui/icons-material/ListAlt";
import GroupsIcon from "@mui/icons-material/Groups";
import EditIcon from "@mui/icons-material/Edit";
import ExtensionTwoToneIcon from "@mui/icons-material/ExtensionTwoTone";
import { TemplateEditorPanel } from "./TemplateEditorPanel.tsx";
import { ConfirmDialog } from "./ConfirmDialog.tsx";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ControlPointOutlinedIcon from "@mui/icons-material/ControlPointOutlined";
import { TaskAttachments, FileKindIcon } from "./TaskAttachments.tsx";
import { formatFileSize } from "../utils/fileApi.ts";
import { useTestMode } from "../hooks/useTestMode.ts";

interface TaskTemplate extends BaseTaskTemplate {
  templateType: "Single" | "Multi" | "Subtask" | string;
}

interface EditingChildTask {
  title: string;
  assignees: string[];
  requesters: string[];
  startDate: string;
  endDate: string;
  memo?: string;
  customColumnValues?: { customColumnId: number; value: string }[];
}

interface BulkRowData {
  id: number;
  title: string;
  assignees: string[];
  requesters: string[];
  priority: Task["priority"];
  notification: boolean;
  tags: string[];
  subtasks: EditingChildTask[];
}

interface ContinuousInputSettings {
  assignees: boolean;
  requesters: boolean;
  priority: boolean;
  tags: boolean;
}

const TitleBulkInput = ({
  onApply,
  onClose,
}: {
  onApply: (titles: string[]) => void;
  onClose: () => void;
}) => {
  const [text, setText] = useState("");
  // メインの保存ボタンの色に合わせる
  const primaryColor = PaletteColorHex["Blue"];

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60]"
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
    >
      <div
        className="bg-white rounded-lg p-6 w-full max-w-md mx-4 space-y-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-bold">タイトルを一括入力</h3>
        <p className="text-sm text-gray-600">
          改行で区切ってタスク名を入力してください。
        </p>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="w-full h-48 p-2 border rounded focus:ring-2"
          style={{ "--tw-ring-color": primaryColor } as React.CSSProperties}
          placeholder={`タスク1
タスク2
タスク3`}
        />
        <div className="flex justify-end space-x-2">
          <button
            onClick={onClose}
            className="px-4 py-2 border rounded hover:bg-gray-100"
          >
            キャンセル
          </button>
          <button
            onClick={() =>
              onApply(text.split("\n").filter((t) => t.trim() !== ""))
            }
            className="px-4 py-2 text-white rounded hover:brightness-105"
            style={{ backgroundColor: primaryColor }}
          >
            適用
          </button>
        </div>
      </div>
    </div>
  );
};

type SubTaskFormProps = {
  task: EditingChildTask;
  index: number;
  updateTask: (index: number, updatedTask: any) => void;
  removeTask: (index: number) => void;
  onShowMemberDialog: (index: number, type: "assignees" | "requesters") => void;
  isInvalid: boolean;
  color: PaletteColor;
  /** プロジェクトに設定されたカスタム項目（設定がある場合のみ期限の横に表示） */
  customColumns?: CustomColumn[];
  /** メモ欄を表示するか（通常作成のサブタスクでのみ true） */
  showMemo?: boolean;
};

const SubTaskForm: React.FC<SubTaskFormProps> = ({
  task,
  index,
  updateTask,
  removeTask,
  onShowMemberDialog,
  isInvalid,
  color,
  customColumns = [],
  showMemo = false,
}) => {
  const hasCustomColumns = customColumns.length > 0;

  const handleCustomFieldChange = (columnId: number, value: string) => {
    const existing = task.customColumnValues ?? [];
    const next = existing.some((v) => v.customColumnId === columnId)
      ? existing.map((v) =>
          v.customColumnId === columnId ? { ...v, value } : v,
        )
      : [...existing, { customColumnId: columnId, value }];
    updateTask(index, { ...task, customColumnValues: next });
  };
  const [isDateSet, setIsDateSet] = useState(
    !!(task.startDate || task.endDate),
  );
  const [isDateRange, setIsDateRange] = useState(
    !!(task.startDate && task.endDate && task.startDate !== task.endDate),
  );

  useEffect(() => {
    const hasDate = !!(task.startDate || task.endDate);
    setIsDateSet(hasDate);
    if (hasDate) {
      setIsDateRange(
        !!(task.startDate && task.endDate && task.startDate !== task.endDate),
      );
    }
  }, [task.startDate, task.endDate]);

  const handleDateChange = (field: "startDate" | "endDate", value: string) => {
    const newDateIso = value ? ymdToUtcIso(value) : "";
    let newStartDate = field === "startDate" ? newDateIso : task.startDate;
    let newEndDate = field === "endDate" ? newDateIso : task.endDate;

    // 「期間」がOFFの場合、終了日は常の開始日と同じにする
    if (!isDateRange) {
      if (field === "startDate") {
        newEndDate = newDateIso;
      } else {
        newStartDate = newDateIso;
      }
    }

    // 開始日と終了日が逆転したら入れ替える
    if (newStartDate && newEndDate && newStartDate > newEndDate) {
      [newStartDate, newEndDate] = [newEndDate, newStartDate];
    }

    updateTask(index, {
      ...task,
      startDate: newStartDate,
      endDate: newEndDate,
    });
  };

  const handleSetDate = (shouldSet: boolean) => {
    setIsDateSet(shouldSet);
    if (!shouldSet) {
      // 未設定にする
      setIsDateRange(false);
      updateTask(index, { ...task, startDate: "", endDate: "" });
    } else {
      // 新たに設定する（今日の日付をデフォルトに）
      const todayIso = ymdToUtcIso(todayYmd());
      updateTask(index, { ...task, startDate: todayIso, endDate: todayIso });
    }
  };

  const handleSetRange = (shouldRange: boolean) => {
    setIsDateRange(shouldRange);
    if (!shouldRange) {
      // 期間をOFFにしたら、終了日を開始日に合わせる
      updateTask(index, { ...task, endDate: task.startDate });
    } else if (task.startDate && task.endDate === task.startDate) {
      // 期間をONにして、開始日と終了日が同じなら、終了日を1日後にする
      const endIso = addDaysIso(task.startDate, 1);
      updateTask(index, { ...task, endDate: endIso });
    }
  };

  return (
    <div className="p-3 border rounded-lg bg-gray-50 relative space-y-3">
      <button
        onClick={() => removeTask(index)}
        className="absolute top-1 right-1 text-gray-400 hover:text-red-500"
        aria-label="削除"
      >
        <CloseIcon fontSize="inherit" sx={{ fontSize: 16 }} />
      </button>

      {/* 行1: タイトル・担当者・依頼者 */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-start pr-5">
        <input
          type="text"
          placeholder="サブタスク名"
          value={task.title}
          onChange={(e) => updateTask(index, { ...task, title: e.target.value })}
          className={`md:col-span-3 p-2 border rounded mt-5 ${
            isInvalid ? "border-red-500" : ""
          }`}
        />

        <div>
          <div className="text-xs text-gray-500 mb-1">
            担当者 ({task.assignees?.length || 0}名)
          </div>
          <button
            onClick={() => onShowMemberDialog(index, "assignees")}
            className="w-full text-left p-2 border rounded bg-white text-sm hover:bg-gray-50 truncate"
            style={{ color: PaletteColorHex[color] }}
          >
            <PersonAddTwoToneIcon fontSize="small" className="mb-1 mr-1" />
            追加
          </button>
        </div>
        <div>
          <div className="text-xs text-gray-500 mb-1">
            依頼者 ({task.requesters?.length || 0}名)
          </div>
          <button
            onClick={() => onShowMemberDialog(index, "requesters")}
            className="w-full text-left p-2 border rounded bg-white text-sm hover:bg-gray-50 truncate"
            style={{ color: PaletteColorHex[color] }}
          >
            <PersonAddTwoToneIcon fontSize="small" className="mb-1 mr-1" />
            追加
          </button>
        </div>
      </div>

      {/* 行2: 左=期限＋カスタム項目（期限の下） / 右=メモ */}
      <div
        className={`grid grid-cols-1 gap-3 items-stretch ${
          showMemo ? "md:grid-cols-2" : ""
        }`}
      >
        {/* 左: 期限＋カスタム項目（プロジェクトに設定がある場合のみ期限の下に表示） */}
        <div className="space-y-3">
          {/* --- 日付入力セクション --- */}
          <div>
            <div className="text-xs text-gray-500 mb-1">期限</div>
          {isDateSet ? (
            <div className="flex items-center space-x-2">
              <input
                type="date"
                value={task.startDate ? isoToYmd(task.startDate) : ""}
                onChange={(e) => handleDateChange("startDate", e.target.value)}
                className="w-full p-2 border rounded text-sm"
              />
              {isDateRange && (
                <>
                  <span className="text-gray-500">～</span>
                  <input
                    type="date"
                    value={task.endDate ? isoToYmd(task.endDate) : ""}
                    onChange={(e) =>
                      handleDateChange("endDate", e.target.value)
                    }
                    className="w-full p-2 border rounded text-sm"
                  />
                </>
              )}
            </div>
          ) : (
            <button
              onClick={() => handleSetDate(true)}
              className="w-full text-left p-2 border rounded bg-white text-sm hover:bg-gray-50"
              style={{ color: PaletteColorHex[color] }}
            >
              ＋ 期限を設定
            </button>
          )}
          <div className="flex items-center gap-2 mt-2 ml-3">
            <FormControlLabel
              control={
                <Switch
                  checked={!isDateSet}
                  onChange={(e) => handleSetDate(!e.target.checked)}
                  size="small"
                />
              }
              label={<span className="text-xs">未設定にする</span>}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={isDateRange}
                  disabled={!isDateSet}
                  onChange={(e) => handleSetRange(e.target.checked)}
                  size="small"
                />
              }
              label={<span className="text-xs">期間を設ける</span>}
            />
          </div>
        </div>

        {/* --- カスタム項目（期限の下） --- */}
        {hasCustomColumns && (
          <div>
            <div className="flex items-center gap-2 text-gray-500 mb-1">
              <ExtensionTwoToneIcon sx={{ fontSize: 14 }} />
              <span className="text-xs">カスタム項目</span>
            </div>
            <div className="space-y-2">
              {customColumns.map((col) => {
                const currentValue =
                  task.customColumnValues?.find(
                    (v) => v.customColumnId === col.customColumnId,
                  )?.value ?? "";
                return (
                  <div
                    key={col.customColumnId}
                    className="flex items-center gap-2"
                  >
                    <span className="text-xs text-gray-500 w-24 flex-shrink-0 truncate">
                      {col.columnName}
                    </span>
                    {col.columnType === "select" ? (
                      <select
                        value={currentValue}
                        onChange={(e) =>
                          handleCustomFieldChange(
                            col.customColumnId,
                            e.target.value,
                          )
                        }
                        className="flex-1 px-2 py-1 border rounded text-xs bg-white"
                      >
                        <option value="">（未選択）</option>
                        {col.options.map((opt) => (
                          <option key={opt.optionId} value={opt.optionValue}>
                            {opt.optionValue}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={currentValue}
                        onChange={(e) =>
                          handleCustomFieldChange(
                            col.customColumnId,
                            e.target.value,
                          )
                        }
                        placeholder={`${col.columnName}を入力...`}
                        className="flex-1 px-2 py-1 border rounded text-xs"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        </div>

        {/* 右: メモ（左のカスタム項目の分だけ高さが伸びる） */}
        {showMemo && (
          <div className="flex flex-col h-full">
            <div className="flex items-center gap-2 text-gray-500 mb-1">
              <StickyNote2TwoToneIcon sx={{ fontSize: 14 }} />
              <span className="text-xs">メモ</span>
            </div>
            <textarea
              placeholder="メモを入力..."
              value={task.memo ?? ""}
              onChange={(e) =>
                updateTask(index, { ...task, memo: e.target.value })
              }
              className="w-full flex-1 p-2 border rounded text-sm min-h-[60px]"
            />
          </div>
        )}
      </div>
    </div>
  );
};

const SubtaskEditModal = ({
  parentTaskTitle,
  initialSubtasks,
  onSave,
  onClose,
  roomMembers,
  color,
}: {
  parentTaskTitle: string;
  initialSubtasks: EditingChildTask[];
  onSave: (updatedSubtasks: EditingChildTask[]) => void;
  onClose: () => void;
  roomMembers: User[];
  color: PaletteColor;
}) => {
  const [subtasks, setSubtasks] = useState<EditingChildTask[]>([]);
  const [memberDialogContext, setMemberDialogContext] = useState<{
    index: number;
    type: "assignees" | "requesters";
  } | null>(null);

  useEffect(() => {
    const lastSubtask = subtasks[subtasks.length - 1];
    if (!lastSubtask || lastSubtask.title.trim() !== "") {
      setSubtasks((current) => [
        ...current,
        {
          title: "",
          assignees: [],
          requesters: [],
          startDate: "",
          endDate: "",
        },
      ]);
    }
  }, [subtasks]);

  useEffect(() => {
    setSubtasks(initialSubtasks.length > 0 ? [...initialSubtasks] : []);
  }, [initialSubtasks]);

  const updateSubtask = (index: number, updatedTask: EditingChildTask) => {
    setSubtasks((current) =>
      current.map((st, i) => (i === index ? updatedTask : st)),
    );
  };

  const removeSubtask = (index: number) => {
    setSubtasks((current) => current.filter((_, i) => i !== index));
  };

  const handleMemberSave = (selectedNames: string[]) => {
    if (!memberDialogContext) return;
    updateSubtask(memberDialogContext.index, {
      ...subtasks[memberDialogContext.index],
      [memberDialogContext.type]: selectedNames,
    });
    setMemberDialogContext(null);
  };

  const handleSaveChanges = () => {
    onSave(subtasks.filter((st) => st.title.trim() !== ""));
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-[70] p-4"
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
    >
      {memberDialogContext && (
        <MemberSelectionDialog
          roomMembers={roomMembers}
          initialSelected={
            subtasks[memberDialogContext.index][
              memberDialogContext.type as "assignees" | "requesters"
            ]
          }
          onSave={handleMemberSave}
          onClose={() => setMemberDialogContext(null)}
          dialogType={memberDialogContext.type}
        />
      )}
      <div
        className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[80vh] flex flex-col shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex-shrink-0">
          <h3 className="font-bold text-lg">サブタスクの編集</h3>
          <p className="text-sm text-gray-500 mt-1">
            親タスク: {parentTaskTitle}
          </p>
        </div>
        <div className="mt-4 space-y-3 overflow-y-auto flex-grow pr-2">
          {subtasks.map((sub, index) => (
            <SubTaskForm
              key={index}
              task={sub}
              index={index}
              updateTask={updateSubtask}
              removeTask={removeSubtask}
              onShowMemberDialog={(idx, type) =>
                setMemberDialogContext({ index: idx, type })
              }
              isInvalid={false}
              color={color}
            />
          ))}
        </div>
        <div className="flex justify-end space-x-2 mt-6 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 border rounded hover:bg-gray-100"
          >
            キャンセル
          </button>
          <button
            onClick={handleSaveChanges}
            className="px-4 py-2 text-white rounded hover:brightness-105"
            style={{ backgroundColor: PaletteColorHex[color] }}
          >
            完了
          </button>
        </div>
      </div>
    </div>
  );
};

const TemplatePreviewModal = ({
  template,
  onClose,
}: {
  template: TaskTemplate;
  onClose: () => void;
}) => {
  const data = JSON.parse(template.taskData);
  const tags = data.tags?.$values || data.tags || [];
  const assignees = data.assignees?.$values || data.assignees || [];
  const requesters = data.requesters?.$values || data.requesters || [];
  const children = data.childTasks?.$values || data.childTasks || [];
  const displayType =
    template.templateType === "Multi"
      ? "マルチテンプレート（子タスク付き）"
      : template.templateType === "Subtask"
        ? "シングルテンプレート（サブタスク）"
        : "シングルテンプレート（単体タスク）";

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-[80] p-4"
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
    >
      <div
        className="bg-white rounded-lg p-6 w-full max-w-md mx-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-bold text-lg mb-4">
          {data.title || " (タイトルなし)"}
        </h3>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2 text-sm">
          <p>
            <strong>テンプレート名:</strong> {template.templateName}
          </p>
          <p>
            <strong>優先度:</strong> {data.priority || "なし"}
          </p>
          <p>
            <strong>担当者:</strong>
            {assignees.length > 0 ? assignees.join(", ") : "なし"}
          </p>
          <p>
            <strong>依頼者:</strong>
            {requesters.length > 0 ? requesters.join(", ") : "なし"}
          </p>
          <p>
            <strong>タグ:</strong> {tags.length > 0 ? tags.join(", ") : "なし"}
          </p>
          <p>
            <strong>メモ:</strong> {data.memo || "なし"}
          </p>
          {children.length > 0 && (
            <div>
              <strong>サブタスク ({children.length}件):</strong>
              <ul className="list-disc list-inside pl-2 mt-1">
                {children.map((st: any, idx: number) => (
                  <li key={idx}>{st.title || "(タイトルなし)"}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div className="flex justify-end space-x-2 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 border rounded hover:bg-gray-100"
          >
            閉じる
          </button>
        </div>
      </div>
    </div>
  );
};

interface Props {
  task: Task | null;
  initialStatus?: Task["status"];
  onClose: () => void;
  onSave: (taskData: object, isBulk: boolean) => void;
  availableTags: Tag[];
  roomMembers: User[];
  onManageTags?: () => void;
  color: PaletteColor;
  roomId: number;
  honbuId: number;
  compactMobile?: boolean;
  /** 新規作成時にタスクが所属するプロジェクト（リスト）ID。カスタム項目の取得に使用 */
  activeListId?: number | null;
}

export function TaskDialog({
  task,
  initialStatus,
  onClose,
  onSave,
  availableTags,
  roomMembers,
  onManageTags,
  color,
  roomId,
  honbuId,
  compactMobile = false,
  activeListId,
}: Props) {
  const isDarkMode = useDarkMode();
  const isTestMode = useTestMode();
  const [taskType, setTaskType] = useState<"single" | "multi">(
    task?.type || "single",
  );
  const isMyList = roomId === 0;

  // --- compact モード ---
  const [compactExpanded, setCompactExpanded] = useState(false);
  const nameInputRef = useRef<HTMLInputElement | null>(null);
  const isCompactActive = compactMobile && !compactExpanded && !task;
  useEffect(() => {
    if (!isCompactActive) return;
    let mounted = true;
    const el = nameInputRef.current;
    const tryFocus = () => {
      if (!mounted || !el) return;
      try {
        el.focus?.({ preventScroll: true } as any);
      } catch {}
      try {
        el.setSelectionRange?.(el.value?.length ?? 0, el.value?.length ?? 0);
      } catch {}
      try {
        el.click?.();
      } catch {}
      try {
        el.scrollIntoView({ block: "center", behavior: "auto" } as any);
      } catch {}
    };
    requestAnimationFrame(tryFocus);
    const t1 = window.setTimeout(tryFocus, 60);
    const t2 = window.setTimeout(tryFocus, 300);
    return () => {
      mounted = false;
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [isCompactActive]);

  const [formData, setFormData] = useState({
    title: task?.title || "",
    startDate: task?.startDate || "",
    endDate: task?.endDate || "",
    tags: (task?.tags || []).map((t) => t.name),
    memo: task?.memo || "",
    notification: task?.notification || false,
    priority: task?.priority || "なし",
    assignees: isMyList ? [] : (task?.assignees || []).map((u) => u.name),
    requesters: isMyList ? [] : (task?.requesters || []).map((u) => u.name),
  });

  const [childTasks, setChildTasks] = useState<EditingChildTask[]>(
    task?.childTasks && task.childTasks.length > 0
      ? task.childTasks.map((t) => ({
          title: t.title,
          assignees: isMyList ? [] : (t.assignees || []).map((u) => u.name),
          requesters: isMyList ? [] : (t.requesters || []).map((u) => u.name),
          startDate: t.startDate || "",
          endDate: t.endDate || "",
        }))
      : [],
  );

  // bulkRowsの型と初期値を更新
  const [bulkRows, setBulkRows] = useState<BulkRowData[]>([
    {
      id: 1,
      title: "",
      assignees: [],
      requesters: [],
      priority: "なし",
      notification: false,
      tags: [],
      subtasks: [],
    },
  ]);

  // 確認ダイアログ用のstate
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    message: string;
    onConfirm: () => void;
  }>({ isOpen: false, message: "", onConfirm: () => {} });

  // 連続入力設定用のStateを追加
  const [continuousSettings, setContinuousSettings] =
    useState<ContinuousInputSettings>({
      assignees: false,
      requesters: false,
      priority: false,
      tags: false,
    });

  // カスタム項目（プロジェクトに定義されたカスタム列）と入力値
  const [customColumns, setCustomColumns] = useState<CustomColumn[]>([]);
  const [customValues, setCustomValues] = useState<Record<number, string>>({});

  // F017 添付ファイル（新規作成時のみ）
  // pendingFiles: 選択したローカルファイル。タスク作成後に呼び出し元がアップロードする
  // templateFiles: テンプレートから引き継ぐ既存ファイル参照（実体コピーなし・同一ファイルへリンク）
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [templateFiles, setTemplateFiles] = useState<
    { fileId: string; fileName: string }[]
  >([]);
  const attachInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // 新規作成時のみ、所属プロジェクトのカスタム項目定義を取得する
    if (task || !activeListId) {
      setCustomColumns([]);
      return;
    }
    fetch(`/api/todolists/${activeListId}/custom-columns?honbuId=${honbuId}`)
      .then((res) => (res.ok ? res.json() : []))
      .then((data: CustomColumn[]) =>
        setCustomColumns(Array.isArray(data) ? data : []),
      )
      .catch(() => setCustomColumns([]));
  }, [activeListId, honbuId, task]);

  const handleCustomFieldChange = (columnId: number, value: string) => {
    setCustomValues((prev) => ({ ...prev, [columnId]: value }));
  };

  useEffect(() => {
    if (!task) {
      if (childTasks.length > 0) setTaskType("multi");
      else setTaskType("single");
    }
  }, [childTasks, task]);
  const [showTitleBulk, setShowTitleBulk] = useState(false);

  const [editingSubtasksForIndex, setEditingSubtasksForIndex] = useState<
    number | null
  >(null);

  const [isDateSet, setIsDateSet] = useState(
    !!(task?.startDate || task?.endDate),
  );
  const [isDateRange, setIsDateRange] = useState(
    !!(task?.startDate && task?.endDate && task?.startDate !== task?.endDate),
  );

  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const [memberDialogContext, setMemberDialogContext] = useState<{
    mode: "main" | "subtask" | "bulk";
    type: "assignees" | "requesters";
    index?: number;
  } | null>(null);

  const [openPriorityIndex, setOpenPriorityIndex] = useState<number | null>(
    null,
  );
  const [openTagIndex, setOpenTagIndex] = useState<number | null>(null);
  const priorityRef = useRef<HTMLDivElement>(null);
  const tagRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (task) {
      setIsDateSet(!!(task.startDate || task.endDate));
      setIsDateRange(
        !!(task.startDate && task.endDate && task.startDate !== task.endDate),
      );
    } else {
      setIsDateSet(false);
      setIsDateRange(false);
      setFormData((prev) => ({ ...prev, startDate: "", endDate: "" }));
    }
  }, [task]);

  const handleChange = (updates: Partial<typeof formData>) => {
    setFormData((prev) => {
      let next = { ...prev, ...updates };
      if (next.startDate && next.endDate && next.startDate > next.endDate) {
        [next.startDate, next.endDate] = [next.endDate, next.startDate];
      }
      return next;
    });
  };

  const handleTagSelection = (tagName: string) => {
    const current = formData.tags;
    const newSelection = current.includes(tagName)
      ? current.filter((name) => name !== tagName)
      : [...current, tagName];
    handleChange({ tags: newSelection });
  };

  const handleMembersSave = (selectedNames: string[]) => {
    if (!memberDialogContext) return;
    const { mode, type, index } = memberDialogContext;

    if (mode === "bulk" && typeof index === "number") {
      const newRows = [...bulkRows];
      (newRows[index] as any)[type] = selectedNames;
      setBulkRows(newRows);
    } else if (mode === "subtask" && typeof index === "number") {
      const newChildTasks = [...childTasks] as any[];
      newChildTasks[index][type] = selectedNames;
      setChildTasks(newChildTasks);
    } else {
      handleChange({ [type]: selectedNames });
    }
    setMemberDialogContext(null);
  };

  const handleAddSubtask = () => {
    setChildTasks((prev) => [
      ...prev,
      {
        title: "",
        assignees: [],
        requesters: [],
        startDate: "",
        endDate: "",
      },
    ]);
    if (!task && taskType === "single") setTaskType("multi");
  };

  const handleBulkTagToggle = (rowIndex: number, tagName: string) => {
    const newRows = [...bulkRows];
    const currentTags = newRows[rowIndex].tags;
    if (currentTags.includes(tagName)) {
      newRows[rowIndex].tags = currentTags.filter((t) => t !== tagName);
    } else {
      newRows[rowIndex].tags.push(tagName);
    }
    setBulkRows(newRows);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        priorityRef.current &&
        !priorityRef.current.contains(event.target as Node)
      ) {
        setOpenPriorityIndex(null);
      }
      if (tagRef.current && !tagRef.current.contains(event.target as Node)) {
        setOpenTagIndex(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const priorityConfig: {
    [key in Task["priority"]]: { label: string; color: string };
  } = {
    なし: { label: "なし", color: "bg-gray-400" },
    低: { label: "低", color: "bg-blue-500" },
    中: { label: "中", color: "bg-orange-500" },
    高: { label: "高", color: "bg-red-500" },
  };

  const [view, setView] = useState<"launcher" | "single" | "bulk" | "template">(
    "single",
  );

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const pristineRef = useRef({
    saved: true, // 保存後 or 開いた直後フラグ
  });

  const computeDirty = () => {
    // 通常フォーム
    const hasMainInput =
      formData.title.trim() !== "" ||
      formData.memo.trim() !== "" ||
      formData.assignees.length > 0 ||
      formData.requesters.length > 0 ||
      formData.tags.length > 0 ||
      (!!formData.startDate && !!formData.endDate);

    // サブタスク
    const hasChildInput = childTasks.some(
      (c) =>
        c.title.trim() !== "" ||
        c.assignees.length > 0 ||
        c.requesters.length > 0 ||
        (!!c.startDate && !!c.endDate),
    );

    // 一括作成 (最後の空行は無視)
    const effectiveBulkRows = bulkRows.slice(0, -1);
    const hasBulkInput = effectiveBulkRows.some(
      (r) =>
        r.title.trim() !== "" ||
        r.assignees.length > 0 ||
        r.requesters.length > 0 ||
        r.tags.length > 0 ||
        r.subtasks.some(
          (s) =>
            s.title.trim() !== "" ||
            s.assignees.length > 0 ||
            s.requesters.length > 0 ||
            (!!s.startDate && !!s.endDate),
        ),
    );

    return hasMainInput || hasChildInput || hasBulkInput;
  };

  useEffect(() => {
    // テンプレート一覧の表示切替(view)だけでは汚染扱いにしない
    const dirty = computeDirty();
    setHasUnsavedChanges(dirty && !pristineRef.current.saved);
  }, [formData, childTasks, bulkRows]);

  // 保存後にクリーン扱い
  const markPristine = () => {
    pristineRef.current.saved = true;
    setHasUnsavedChanges(false);
  };

  // 何か入力されたらダーティ扱い開始 (最初の入力契機)
  useEffect(() => {
    if (computeDirty()) {
      pristineRef.current.saved = false;
    }
  }, [formData, childTasks, bulkRows]);

  const attemptClose = () => {
    if (hasUnsavedChanges) {
      setConfirmDialog({
        isOpen: true,
        message: "未保存の変更があります。閉じてもよろしいですか？",
        onConfirm: () => {
          setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
          onClose();
        },
      });
    } else {
      onClose();
    }
  };

  const handleSave = () => {
    const errors: string[] = [];
    const validChildTasks = childTasks.filter((c) => c.title.trim() !== "");
    const finalTaskType: "single" | "multi" =
      validChildTasks.length > 0 ? "multi" : "single";

    if (view === "single") {
      if (!formData.title.trim()) errors.push("title");
      if (finalTaskType === "multi") {
        validChildTasks.forEach((ct, idx) => {
          if (!ct.title.trim()) errors.push(`child-${idx}`);
        });
      }
    } else if (view === "bulk") {
      if (finalTaskType === "multi" && !formData.title.trim())
        errors.push("parent-title");
      const validRows = bulkRows.filter((r) => r.title.trim() !== "");
      if (validRows.length === 0) {
        bulkRows.forEach((_, idx) => errors.push(`bulk-${idx}`));
      }
    }

    if (errors.length) {
      setValidationErrors(errors);
      return;
    }
    setValidationErrors([]);

    if (view === "bulk") {
      const finalType = finalTaskType;
      if (finalType === "single") {
        onSave(
          {
            type: "single",
            rows: bulkRows.filter((r) => r.title.trim() !== ""),
          },
          true,
        );
      } else {
        onSave(
          {
            type: "multi",
            parentData: {
              title: formData.title,
              priority: formData.priority,
              notification: formData.notification,
              tags: formData.tags,
            },
            rows: bulkRows.filter((r) => r.title.trim() !== ""),
          },
          true,
        );
      }
      markPristine(); // ← 追加
    } else {
      onSave(
        {
          ...formData,
          type: finalTaskType,
          status: initialStatus,
          startDate: isDateSet ? formData.startDate : "",
          endDate:
            isDateSet && isDateRange
              ? formData.endDate
              : isDateSet
                ? formData.startDate
                : "",
          childTasks: finalTaskType === "multi" ? validChildTasks : [],
          // カスタム項目の入力値（未入力の項目は除外）
          customColumnValues: customColumns
            .map((c) => ({
              customColumnId: c.customColumnId,
              value: customValues[c.customColumnId] ?? "",
            }))
            .filter((v) => v.value !== ""),
          // F017: テンプレート由来の既存ファイル参照（作成 API の fileIds に渡す）
          fileIds: templateFiles.map((f) => f.fileId),
          // F017: 選択済みローカルファイル（タスク作成後に呼び出し元がアップロードする）
          pendingFiles: pendingFiles,
        },
        false,
      );
      markPristine();
    }
  };

  let initialMemberSelection: string[] = [];
  if (memberDialogContext) {
    const { mode, type, index } = memberDialogContext;
    if (mode === "bulk" && typeof index === "number")
      initialMemberSelection = (bulkRows[index] as any)[type] || [];
    else if (mode === "subtask" && typeof index === "number")
      initialMemberSelection = (childTasks[index] as any)[type] || [];
    else if (mode === "main") initialMemberSelection = formData[type];
  }

  const handleSubtasksSave = (updatedSubtasks: EditingChildTask[]) => {
    if (editingSubtasksForIndex === null) return;
    const newRows = [...bulkRows];
    newRows[editingSubtasksForIndex].subtasks = updatedSubtasks;
    setBulkRows(newRows);
    setEditingSubtasksForIndex(null); // モーダルを閉じる
  };
  const [templates, setTemplates] = useState<TaskTemplate[]>([]);
  const [otherRoomTemplates, setOtherRoomTemplates] = useState<TaskTemplate[]>(
    [],
  );
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(true);
  const [previewingTemplate, setPreviewingTemplate] =
    useState<TaskTemplate | null>(null);

  const normalizeTemplateChildren = (rawChildren: any): any[] => {
    if (!rawChildren) return [];
    if (Array.isArray(rawChildren)) return rawChildren;
    if (Array.isArray(rawChildren.$values)) return rawChildren.$values;
    return [];
  };

  useEffect(() => {
    const fetchTemplates = async () => {
      setIsLoadingTemplates(true);
      try {
        // 現在のルームのテンプレートを取得
        const res = await fetch(
          `/api/templates?roomId=${roomId}&honbuId=${honbuId}`,
        );
        if (res.ok) {
          const data = await res.json();
          const templatesWithType = data.map((template: any) => {
            const taskData = JSON.parse(template.taskData || "{}");
            const childTasks = normalizeTemplateChildren(taskData.childTasks);
            return {
              ...template,
              templateType: childTasks.length > 0 ? "Multi" : "Single",
            };
          });
          setTemplates(templatesWithType);
        }

        // 別ルームで自分が作成したテンプレートを取得
        const otherRes = await fetch(
          `/api/templates/other-rooms?honbuId=${honbuId}&currentRoomId=${roomId}`,
        );
        if (otherRes.ok) {
          const otherData = await otherRes.json();
          const otherTemplatesWithType = otherData.map((template: any) => {
            const taskData = JSON.parse(template.taskData || "{}");
            const childTasks = normalizeTemplateChildren(taskData.childTasks);
            return {
              ...template,
              templateType: childTasks.length > 0 ? "Multi" : "Single",
            };
          });
          setOtherRoomTemplates(otherTemplatesWithType);
        }
      } catch (error) {
        console.error("Failed to fetch templates:", error);
      } finally {
        setIsLoadingTemplates(false);
      }
    };

    if (view === "template") {
      fetchTemplates();
    }
  }, [roomId, honbuId, view]);

  const [editingTemplate, setEditingTemplate] = useState<TaskTemplate | null>(
    null,
  );

  const handleTemplateUpdated = (
    updatedTemplateId: number,
    newTaskData: string,
  ) => {
    const mapTemplate = (t: TaskTemplate): TaskTemplate => {
      if (t.templateId !== updatedTemplateId) return t;
      const taskData = JSON.parse(newTaskData || "{}");
      const childTasks = normalizeTemplateChildren(taskData.childTasks);
      return {
        ...t,
        taskData: newTaskData,
        templateType: childTasks.length > 0 ? "Multi" : "Single",
      };
    };
    setTemplates((currentTemplates) => currentTemplates.map(mapTemplate));
  };

  const offsetToIso = (offset: number): string => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + offset);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const da = String(d.getDate()).padStart(2, "0");
    return ymdToUtcIso(`${y}-${m}-${da}`);
  };

  const idToMemberName = (id: number) =>
    roomMembers.find((m) => Number(m.id) === id)?.name ?? null;

  const idToTagName = (id: number) =>
    availableTags.find((t) => t.id === id)?.name ?? null;

  const applyTemplate = (template: TaskTemplate) => {
    const data = JSON.parse(template.taskData || "{}");
    setFormData((f) => ({
      ...f,
      title: data.title || "",
      memo: data.memo || "",
      priority: data.priority || "なし",
      notification: !!data.notification,
      assignees: (data.assigneeHonbuIds || [])
        .map(idToMemberName)
        .filter(Boolean) as string[],
      requesters: (data.requesterHonbuIds || [])
        .map(idToMemberName)
        .filter(Boolean) as string[],
      tags: (data.tagIds || []).map(idToTagName).filter(Boolean) as string[],
      startDate:
        data.startDateOffset != null ? offsetToIso(data.startDateOffset) : "",
      endDate:
        data.endDateOffset != null ? offsetToIso(data.endDateOffset) : "",
    }));
    // カスタム項目: 現在のプロジェクトに定義が存在する列のみ反映する。
    // テンプレートの customColumnId は元プロジェクトの列ID。別プロジェクトの列は
    // 現在の customColumns に含まれないため自動的に除外され、「同一プロジェクトのみ反映」となる。
    const validColumnIds = new Set(customColumns.map((col) => col.customColumnId));
    const extractCustomValues = (raw: any): Record<number, string> => {
      const list = raw?.$values || raw || [];
      const result: Record<number, string> = {};
      (list as any[]).forEach((v) => {
        if (
          v &&
          validColumnIds.has(v.customColumnId) &&
          v.value != null &&
          v.value !== ""
        ) {
          result[v.customColumnId] = String(v.value);
        }
      });
      return result;
    };
    setCustomValues(extractCustomValues(data.customColumnValues));

    const children = (data.childTasks || []).map((c: any) => {
      const childValues = extractCustomValues(c.customColumnValues);
      return {
        title: c.title || "",
        assignees: (c.assigneeHonbuIds || [])
          .map(idToMemberName)
          .filter(Boolean),
        requesters: (c.requesterHonbuIds || [])
          .map(idToMemberName)
          .filter(Boolean),
        startDate:
          c.startDateOffset != null ? offsetToIso(c.startDateOffset) : "",
        endDate: c.endDateOffset != null ? offsetToIso(c.endDateOffset) : "",
        customColumnValues: Object.entries(childValues).map(
          ([customColumnId, value]) => ({
            customColumnId: Number(customColumnId),
            value,
          }),
        ),
      };
    });
    setChildTasks(children);
    setTaskType(children.length > 0 ? "multi" : "single");

    // F017: テンプレートの添付ファイル参照を引き継ぐ（新規作成時のみ有効）
    const templateTaskFiles = data.taskFiles?.$values || data.taskFiles || [];
    setTemplateFiles(
      (templateTaskFiles as any[])
        .filter((f) => f && f.fileId)
        .map((f) => ({ fileId: String(f.fileId), fileName: f.fileName || "" })),
    );

    setView("single");
    // 日付フラグ更新
    const hasDates = !!(data.startDateOffset != null);
    setIsDateSet(hasDates);
    setIsDateRange(false);
    setTimeout(() => markPristine(), 0);
  };

  const handleSelectTemplate = (template: TaskTemplate) => {
    const templateTask = JSON.parse(template.taskData);
    const today = todayYmd();

    const calculateRelativeDateIso = (
      offsetDays: number | null | undefined,
    ) => {
      // null または undefined の場合のみ「未設定」とする (0 は「当日」として扱う)
      if (offsetDays === null || offsetDays === undefined) return "";

      try {
        const d = new Date();
        d.setHours(0, 0, 0, 0); // 今日の0時
        d.setDate(d.getDate() + offsetDays); // オフセットを加算

        // YYYY-MM-DD形式に変換
        const year = d.getFullYear();
        const month = (d.getMonth() + 1).toString().padStart(2, "0");
        const day = d.getDate().toString().padStart(2, "0");
        const ymd = `${year}-${month}-${day}`;

        return ymdToUtcIso(ymd);
      } catch (e) {
        console.error("Date calculation failed:", e);
        return "";
      }
    };

    const tags = templateTask.tags?.$values || templateTask.tags || [];
    const assignees =
      templateTask.assignees?.$values || templateTask.assignees || [];
    const requesters =
      templateTask.requesters?.$values || templateTask.requesters || [];
    // --- サブタスク情報を取得 ---
    const childTasksFromTemplate =
      templateTask.childTasks?.$values || templateTask.childTasks || [];

    setFormData({
      title: templateTask.title || "",
      startDate: calculateRelativeDateIso(templateTask.startDateOffset),
      endDate: calculateRelativeDateIso(templateTask.endDateOffset),
      memo: templateTask.memo || "",
      notification: templateTask.notification || false,
      priority: templateTask.priority || "なし",
      tags: tags,
      assignees: assignees,
      requesters: requesters,
    });

    // --- サブタスクがあればchildTasksにセットし、taskTypeをmultiに ---
    if (childTasksFromTemplate.length > 0) {
      setChildTasks(
        childTasksFromTemplate.map((st: any) => ({
          title: st.title || "",
          assignees: st.assignees?.$values || st.assignees || [],
          requesters: st.requesters?.$values || st.requesters || [],
          startDate: calculateRelativeDateIso(st.startDateOffset),
          endDate: calculateRelativeDateIso(st.endDateOffset),
        })),
      );
      setTaskType("multi");
    } else {
      setChildTasks([]);
      setTaskType("single");
    }

    // F017: テンプレートの添付ファイル参照を引き継ぐ
    const templateTaskFiles =
      templateTask.taskFiles?.$values || templateTask.taskFiles || [];
    setTemplateFiles(
      (templateTaskFiles as any[])
        .filter((f) => f && f.fileId)
        .map((f) => ({ fileId: String(f.fileId), fileName: f.fileName || "" })),
    );

    setView("single");
  };

  const handleDeleteTemplate = async (
    e: React.MouseEvent,
    templateId: number,
  ) => {
    e.stopPropagation();

    setConfirmDialog({
      isOpen: true,
      message: "このテンプレートを削除してもよろしいですか？",
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
        try {
          const response = await fetch(`/api/templates/${templateId}`, {
            method: "DELETE",
          });

          if (response.ok) {
            setTemplates((currentTemplates) =>
              currentTemplates.filter((t) => t.templateId !== templateId),
            );
          } else {
            alert("テンプレートの削除に失敗しました。");
          }
        } catch (error) {
          console.error("Failed to delete template:", error);
          alert("テンプレートの削除中にエラーが発生しました。");
        }
      },
    });
  };

  const handleToggleShare = async (
    e: React.MouseEvent,
    template: TaskTemplate,
  ) => {
    e.stopPropagation();
    const newIsShare = !template.isShare;
    try {
      const response = await fetch(
        `/api/templates/${template.templateId}/share?honbuId=${honbuId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ IsShare: newIsShare }),
        },
      );
      if (response.ok) {
        setTemplates((current) =>
          current.map((t) =>
            t.templateId === template.templateId
              ? { ...t, isShare: newIsShare }
              : t,
          ),
        );
      } else {
        alert("共有設定の変更に失敗しました。");
      }
    } catch (error) {
      console.error("Failed to update share setting:", error);
      alert("エラーが発生しました。");
    }
  };

  const [subtaskTemplates, setSubtaskTemplates] = useState<TaskTemplate[]>([]);
  const [showSubtaskTemplates, setShowSubtaskTemplates] = useState(false);
  const templateMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchSubtaskTemplates = async () => {
      try {
        const res = await fetch(
          `/api/templates?roomId=${roomId}&honbuId=${honbuId}`,
        );
        if (res.ok) {
          const data = await res.json();
          const templatesWithType = data.map((template: any) => {
            const taskData = JSON.parse(template.taskData);
            return {
              ...template,
              templateType:
                taskData.childTasks?.length > 0 ? "Multi" : "Single",
            };
          });
          setSubtaskTemplates(templatesWithType);
        }
      } catch (error) {
        console.error("Failed to fetch subtask templates:", error);
      }
    };
    fetchSubtaskTemplates();
  }, [roomId, honbuId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        templateMenuRef.current &&
        !templateMenuRef.current.contains(event.target as Node)
      ) {
        setShowSubtaskTemplates(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleApplySubtaskTemplate = (template: TaskTemplate) => {
    const data = JSON.parse(template.taskData || "{}");
    const children = (data.childTasks || []).map((c: any) => ({
      title: c.title || "",
      assignees: (c.assigneeHonbuIds || []).map(idToMemberName).filter(Boolean),
      requesters: (c.requesterHonbuIds || [])
        .map(idToMemberName)
        .filter(Boolean),
      startDate:
        c.startDateOffset != null ? offsetToIso(c.startDateOffset) : "",
      endDate: c.endDateOffset != null ? offsetToIso(c.endDateOffset) : "",
    }));
    // Multi テンプレートなら全子タスク追加 / Single なら単一行
    if (children.length > 0) {
      setChildTasks((curr) => [...curr, ...children]);
      setTaskType("multi");
    } else {
      const single = {
        title: data.title || "",
        assignees: (data.assigneeHonbuIds || [])
          .map(idToMemberName)
          .filter(Boolean),
        requesters: (data.requesterHonbuIds || [])
          .map(idToMemberName)
          .filter(Boolean),
        startDate:
          data.startDateOffset != null ? offsetToIso(data.startDateOffset) : "",
        endDate:
          data.endDateOffset != null ? offsetToIso(data.endDateOffset) : "",
      };
      setChildTasks((curr) => [...curr, single]);
      setTaskType("multi");
    }
    setShowSubtaskTemplates(false);
  };

  useEffect(() => {
    setIsDateSet(!!(formData.startDate || formData.endDate));
    setIsDateRange(
      !!(
        formData.startDate &&
        formData.endDate &&
        formData.startDate !== formData.endDate
      ),
    );
  }, [formData.startDate, formData.endDate]);

  // タグ選択セクション（カスタム項目の有無でレイアウトを切り替えるため切り出し）
  const tagSection = (
    <div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-gray-700">
          <StyleTwoToneIcon fontSize="small" />
          <span className="text-sm font-bold">タグ</span>
        </div>
        <button
          type="button"
          onClick={() => onManageTags?.()}
          className="text-sm hover:underline flex items-center"
          style={{ color: PaletteColorHex[color] }}
        >
          <NewLabelTwoToneIcon
            className="w-4 h-4 mb-0.5 mr-1"
            fontSize="small"
          />
          <span>追加・編集</span>
        </button>
      </div>
      <div className="flex flex-wrap gap-2 mt-2">
        {availableTags.map((tag) => {
          const isSelected = formData.tags.includes(tag.name);
          const hexColor = tag.color || "#3B82F6";
          const tagStyle: React.CSSProperties = {
            transition: "all 0.2s ease-in-out",
            cursor: "pointer",
            ...(isSelected
              ? {
                  backgroundColor: `${hexColor}20`,
                  color: hexColor,
                  border: `1px solid ${hexColor}80`,
                }
              : {
                  backgroundColor: `white`,
                  color: `${hexColor}80`,
                  border: `1px solid ${hexColor}80`,
                }),
          };
          return (
            <div
              key={tag.id}
              onClick={() => handleTagSelection(tag.name)}
              style={tagStyle}
              className="px-3 py-1 rounded-full text-xs font-semibold"
            >
              #{tag.name}
            </div>
          );
        })}
      </div>
    </div>
  );

  // カスタム項目の入力セクション
  const customFieldsSection = (
    <div>
      <div className="flex items-center gap-2 text-gray-700">
        <ExtensionTwoToneIcon fontSize="small" />
        <span className="text-sm font-bold">カスタム項目</span>
      </div>
      <div className="mt-2 space-y-2">
        {customColumns.map((col) => {
          const currentValue = customValues[col.customColumnId] ?? "";
          return (
            <div key={col.customColumnId} className="flex items-center gap-2">
              <span className="text-xs text-gray-500 w-24 flex-shrink-0 truncate">
                {col.columnName}
              </span>
              {col.columnType === "select" ? (
                <select
                  value={currentValue}
                  onChange={(e) =>
                    handleCustomFieldChange(col.customColumnId, e.target.value)
                  }
                  className="flex-1 px-2 py-1 border rounded text-xs bg-white"
                >
                  <option value="">（未選択）</option>
                  {col.options.map((opt) => (
                    <option key={opt.optionId} value={opt.optionValue}>
                      {opt.optionValue}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={currentValue}
                  onChange={(e) =>
                    handleCustomFieldChange(col.customColumnId, e.target.value)
                  }
                  placeholder={`${col.columnName}を入力...`}
                  className="flex-1 px-2 py-1 border rounded text-xs"
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  if (isCompactActive) {
    return (
      <div className="fixed inset-0 z-[70]" aria-modal="true" role="dialog">
        <div className="absolute inset-0 bg-black/50" onClick={attemptClose} />
        <div className="absolute left-0 right-0 bottom-0 z-80">
          <div
            className={`rounded-t-lg p-3 border-t shadow-lg ${
              isDarkMode
                ? "bg-gray-900 border-gray-700 text-gray-200"
                : "bg-white border-gray-200"
            }`}
          >
            <div className="mb-2">
              <input
                ref={nameInputRef}
                type="text"
                value={formData.title}
                onChange={(e) => handleChange({ title: e.target.value })}
                placeholder="タスク名を入力"
                className={`w-full p-3 border rounded-md text-base ${
                  isDarkMode
                    ? "bg-gray-800 border-gray-600 text-gray-200 placeholder-gray-500"
                    : "bg-white border-gray-300"
                }`}
                aria-label="タスク名"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            <div className="flex items-center justify-between">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setCompactExpanded(true);
                  setView("single");
                }}
                className={`px-4 py-2 rounded-md border text-sm ${
                  isDarkMode
                    ? "border-gray-600 hover:bg-gray-700"
                    : "border-gray-300 hover:bg-gray-100"
                }`}
              >
                詳細
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleSave();
                }}
                className="px-4 py-2 rounded-md text-white"
                style={{
                  backgroundColor: isDarkMode
                    ? PaletteColorDarkHex[color]
                    : PaletteColorHex[color],
                }}
              >
                作成
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-0 md:p-4"
      role="dialog"
      aria-modal="true"
      onClick={attemptClose}
    >
      {/* 確認ダイアログ */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title="確認"
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() =>
          setConfirmDialog((prev) => ({ ...prev, isOpen: false }))
        }
        color={color}
      />
      {editingSubtasksForIndex !== null && (
        <SubtaskEditModal
          parentTaskTitle={bulkRows[editingSubtasksForIndex].title}
          initialSubtasks={bulkRows[editingSubtasksForIndex].subtasks}
          onSave={handleSubtasksSave}
          onClose={() => setEditingSubtasksForIndex(null)}
          roomMembers={roomMembers}
          color={color}
        />
      )}
      {memberDialogContext && (
        <MemberSelectionDialog
          roomMembers={roomMembers}
          initialSelected={initialMemberSelection}
          onSave={handleMembersSave}
          onClose={() => setMemberDialogContext(null)}
          dialogType={memberDialogContext.type}
        />
      )}
      {showTitleBulk && (
        <TitleBulkInput
          onClose={() => setShowTitleBulk(false)}
          onApply={(titles) => {
            // 1. 既存の入力済みタスクを取得（空の行は除く）
            const existingRows = bulkRows.filter((r) => r.title.trim() !== "");

            // 2. 一括入力されたタイトルから新しいタスクの配列を作成
            const newRowsFromInput = titles.map((title, i) => ({
              id: Date.now() + i,
              title,
              assignees: [] as string[],
              requesters: [] as string[],
              priority: "なし" as Task["priority"],
              notification: false,
              tags: [] as string[],
              subtasks: [],
            }));

            // 3. 次の入力のための空の行を定義
            const emptyRow = {
              id: Date.now() + titles.length,
              title: "",
              assignees: [] as string[],
              requesters: [] as string[],
              priority: "なし" as Task["priority"],
              notification: false,
              tags: [] as string[],
              subtasks: [],
            };

            // 4. 既存タスク + 新規タスク + 空の行 を結合してstateを更新
            setBulkRows([...existingRows, ...newRowsFromInput, emptyRow]);
            setShowTitleBulk(false);
          }}
        />
      )}
      {previewingTemplate && (
        <TemplatePreviewModal
          template={previewingTemplate}
          onClose={() => setPreviewingTemplate(null)}
        />
      )}

      <div
        className={`w-screen h-screen rounded-none p-4 md:w-full md:max-w-4xl md:max-h-[90vh] md:rounded-lg md:p-6 flex flex-col ${
          isDarkMode ? "bg-gray-900 text-gray-200" : "bg-white text-gray-900"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4 flex-shrink-0">
          <h3 className="text-lg font-bold">
            {task ? "タスクを編集" : "新規タスク作成"}
          </h3>
          <button
            onClick={attemptClose}
            aria-label="閉じる"
            className={isDarkMode ? "text-gray-400 hover:text-gray-200" : ""}
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>

        {!task && (
          <div
            className={`flex items-center space-x-2 p-1 rounded-lg mx-0 mb-4 ${
              isDarkMode ? "bg-gray-700" : "bg-gray-100"
            }`}
          >
            <button
              onClick={() => setView("single")}
              className={`flex-1 px-3 py-1 rounded-md text-sm font-medium ${
                view === "single"
                  ? isDarkMode
                    ? "bg-gray-800 shadow text-gray-200"
                    : "bg-white shadow"
                  : isDarkMode
                    ? "text-gray-400"
                    : ""
              }`}
            >
              通常作成
            </button>
            <button
              onClick={() => setView("bulk")}
              className={`flex-1 px-3 py-1 rounded-md text-sm font-medium ${
                view === "bulk"
                  ? isDarkMode
                    ? "bg-gray-800 shadow text-gray-200"
                    : "bg-white shadow"
                  : isDarkMode
                    ? "text-gray-400"
                    : ""
              }`}
            >
              一括作成
            </button>
            <button
              onClick={() => setView("template")}
              className={`flex-1 px-3 py-1 rounded-md text-sm font-medium ${
                view === "template"
                  ? isDarkMode
                    ? "bg-gray-800 shadow text-gray-200"
                    : "bg-white shadow"
                  : isDarkMode
                    ? "text-gray-400"
                    : ""
              }`}
            >
              テンプレート
            </button>
          </div>
        )}
        <div className="space-y-4 overflow-y-auto pr-2 flex-grow px-4">
          {view === "single" && (
            <>
              <div
                className={`p-4 border rounded-lg space-y-6 ${
                  isDarkMode ? "border-gray-700" : "border-gray-200"
                }`}
              >
                <div>
                  <label
                    className={`text-sm font-bold ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    タイトル <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleChange({ title: e.target.value })}
                    className={`w-full p-2 border rounded mt-1 ${
                      validationErrors.includes("title") ||
                      validationErrors.includes("parent-title")
                        ? "border-red-500"
                        : isDarkMode
                          ? "bg-gray-800 border-gray-600 text-gray-200"
                          : ""
                    }`}
                  />
                </div>
                {(taskType === "single" || taskType === "multi") &&
                  !isMyList && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <div
                          className={`flex items-center gap-2 mb-1 ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          <PeopleTwoToneIcon fontSize="small" />
                          <span className="text-sm font-bold">
                            担当者 ({formData.assignees.length}名)
                          </span>
                        </div>
                        <div
                          className="flex items-center flex-wrap gap-1 mt-1 p-2 border rounded min-h-[40px] bg-white cursor-pointer"
                          onClick={() =>
                            setMemberDialogContext({
                              mode: "main",
                              type: "assignees",
                            })
                          }
                        >
                          {formData.assignees.map((name) => (
                            <span
                              key={name}
                              className="text-xs px-2 py-1 bg-gray-200 rounded-full"
                            >
                              {name}
                            </span>
                          ))}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setMemberDialogContext({
                                mode: "main",
                                type: "assignees",
                              });
                            }}
                            className="text-sm hover:underline ml-auto"
                            style={{ color: PaletteColorHex[color] }}
                          >
                            <PersonAddTwoToneIcon
                              fontSize="small"
                              className="mb-1 mr-1"
                            />
                            追加
                          </button>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 text-gray-700 mb-1">
                          <RecordVoiceOverTwoToneIcon fontSize="small" />
                          <span className="text-sm font-bold">
                            依頼者 ({formData.requesters.length}名)
                          </span>
                        </div>
                        <div
                          className="flex items-center flex-wrap gap-1 mt-1 p-2 border rounded min-h-[40px] bg-white cursor-pointer"
                          onClick={() =>
                            setMemberDialogContext({
                              mode: "main",
                              type: "requesters",
                            })
                          }
                        >
                          {formData.requesters.map((name) => (
                            <span
                              key={name}
                              className="text-xs px-2 py-1 bg-gray-200 rounded-full"
                            >
                              {name}
                            </span>
                          ))}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setMemberDialogContext({
                                mode: "main",
                                type: "requesters",
                              });
                            }}
                            className="text-sm hover:underline ml-auto"
                            style={{ color: PaletteColorHex[color] }}
                          >
                            <PersonAddTwoToneIcon
                              fontSize="small"
                              className="mb-1 mr-1"
                            />
                            追加
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                <div className="space-y-4">
                  <div className="space-y-4 sm:space-y-0 md:grid md:grid-cols-2 md:gap-6">
                    {/* --- 期限セクション --- */}
                    <div>
                      <div className="flex items-center gap-2 text-gray-700 mb-1">
                        <EventTwoToneIcon fontSize="small" />
                        <span className="text-sm font-bold">期限</span>
                      </div>
                      <div className="mt-1">
                        {isDateSet ? (
                          <div
                            className="flex items-center"
                            style={{
                              minHeight: 38,
                              width: "100%",
                              maxWidth: 395,
                              position: "relative",
                            }}
                          >
                            <input
                              id="main-date-start"
                              type="date"
                              value={
                                formData.startDate
                                  ? isoToYmd(formData.startDate)
                                  : ""
                              }
                              onChange={(e) => {
                                const iso = e.target.value
                                  ? ymdToUtcIso(e.target.value)
                                  : "";
                                const updates: Partial<typeof formData> = {
                                  startDate: iso,
                                  endDate: isDateRange ? formData.endDate : iso,
                                };
                                handleChange(updates);
                              }}
                              className="w-full p-2 border rounded text-sm cursor-pointer"
                            />
                            {isDateRange && (
                              <>
                                <span className="mx-2">～</span>
                                <input
                                  type="date"
                                  value={
                                    formData.endDate
                                      ? isoToYmd(formData.endDate)
                                      : ""
                                  }
                                  onChange={(e) => {
                                    const iso = e.target.value
                                      ? ymdToUtcIso(e.target.value)
                                      : "";

                                    const updates: Partial<typeof formData> = {
                                      endDate: iso,
                                    };
                                    handleChange(updates);
                                  }}
                                  className="w-full p-2 border rounded text-sm cursor-pointer"
                                />
                              </>
                            )}
                          </div>
                        ) : (
                          <div
                            className="p-2 border rounded text-sm text-gray-400 flex items-center justify-center cursor-pointer select-none"
                            style={{
                              minHeight: 38,
                              width: "100%",
                              maxWidth: 395,
                            }}
                            onClick={() => {
                              const todayIso = ymdToUtcIso(todayYmd());
                              setIsDateSet(true);
                              setIsDateRange(false);
                              handleChange({ startDate: todayIso });
                              handleChange({ endDate: todayIso });
                            }}
                          >
                            未設定
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <FormControlLabel
                          control={
                            <Switch
                              checked={!isDateSet}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setIsDateSet(false);
                                  setIsDateRange(false);
                                  handleChange({ startDate: "", endDate: "" });
                                } else {
                                  const todayIso = ymdToUtcIso(todayYmd());
                                  setIsDateSet(true);
                                  setIsDateRange(false);
                                  handleChange({
                                    startDate: todayIso,
                                    endDate: todayIso,
                                  });
                                }
                              }}
                            />
                          }
                          label={<span className="text-sm">未設定にする</span>}
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={isDateRange}
                              disabled={!isDateSet}
                              onChange={(e) => {
                                const on = e.target.checked;
                                setIsDateRange(on);
                                if (on) {
                                  const baseYmd =
                                    isoToYmd(formData.startDate) ||
                                    isoToYmd(new Date().toISOString());
                                  const d = new Date(`${baseYmd}T00:00:00`);
                                  d.setDate(d.getDate() + 1);
                                  const nextYmd = d.toISOString().slice(0, 10);
                                  handleChange({
                                    startDate: ymdToUtcIso(baseYmd),
                                    endDate: ymdToUtcIso(nextYmd),
                                  });
                                } else {
                                  handleChange({ endDate: formData.startDate });
                                }
                              }}
                            />
                          }
                          label={<span className="text-sm">期間を設ける</span>}
                        />
                      </div>
                    </div>

                    {/* --- 重要度セクション --- */}
                    <div>
                      <div className="flex items-center gap-2 mt-0 text-gray-700">
                        <LabelImportantTwoToneIcon fontSize="small" />
                        <span className="text-sm font-bold">重要度</span>
                      </div>
                      <div className="grid grid-cols-4 gap-1 mt-1 rounded-lg bg-gray-200 p-1">
                        {["なし", "高", "中", "低"].map((priority) => (
                          <button
                            key={priority}
                            type="button"
                            onClick={() =>
                              handleChange({
                                priority: priority as Task["priority"],
                              })
                            }
                            className={`rounded-md py-1 text-sm font-semibold transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 ${
                              formData.priority === priority
                                ? "bg-white text-gray-800 shadow"
                                : "bg-transparent text-gray-500 hover:bg-white/60"
                            }`}
                            style={
                              {
                                "--tw-ring-color": PaletteColorHex[color],
                              } as React.CSSProperties
                            }
                          >
                            {priority}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                {customColumns.length > 0 ? (
                  // カスタム項目がある場合：左半分にタグ、右半分にカスタム項目
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {tagSection}
                    {customFieldsSection}
                  </div>
                ) : (
                  tagSection
                )}
                <div>
                  <div>
                    <div className="flex items-center gap-2 text-gray-700 mb-1">
                      <StickyNote2TwoToneIcon fontSize="small" />
                      <span className="text-sm font-bold">メモ</span>
                    </div>
                    <textarea
                      placeholder="メモを入力..."
                      value={formData.memo}
                      onChange={(e) => handleChange({ memo: e.target.value })}
                      className="w-full p-2 border-2 border-gray-200 rounded-lg min-h-[100px]"
                    />
                  </div>
                </div>

                {/* F017: 添付ファイル（テストモード時のみ表示） */}
                {!isTestMode ? null : task ? (
                  // 編集時はタスクが確定しているため、その場でアップロード・削除できる
                  <TaskAttachments
                    taskId={task.id}
                    honbuId={honbuId}
                    color={color}
                  />
                ) : (
                  <div>
                    <div className="flex items-center gap-2 text-gray-700 mb-1">
                      <AttachFileIcon fontSize="small" />
                      <span className="text-sm font-bold">添付ファイル</span>
                      <button
                        type="button"
                        onClick={() => attachInputRef.current?.click()}
                        className="p-0.5 rounded-full hover:bg-gray-100"
                        style={{ color: PaletteColorHex[color] }}
                        title="ファイルを添付する（タスク作成時にアップロード）"
                        aria-label="ファイルを添付する"
                      >
                        <ControlPointOutlinedIcon
                          sx={{ fontSize: 18, display: "block" }}
                        />
                      </button>
                    </div>
                    {(templateFiles.length > 0 || pendingFiles.length > 0) && (
                      <ul className="space-y-1 mb-2">
                        {templateFiles.map((f) => (
                          <li
                            key={f.fileId}
                            className="flex items-center gap-2 px-2 py-1.5 rounded border border-gray-200 bg-gray-50"
                          >
                            <FileKindIcon fileName={f.fileName} />
                            <span
                              className="flex-1 min-w-0 text-sm truncate"
                              title={f.fileName}
                            >
                              {f.fileName}
                            </span>
                            <span className="text-xs text-gray-400 shrink-0">
                              テンプレートから引き継ぎ
                            </span>
                            <button
                              type="button"
                              onClick={() =>
                                setTemplateFiles((prev) =>
                                  prev.filter((x) => x.fileId !== f.fileId),
                                )
                              }
                              className="p-1 rounded text-red-400 hover:text-red-600 shrink-0"
                              aria-label="添付を外す"
                            >
                              <CloseIcon sx={{ fontSize: 16 }} />
                            </button>
                          </li>
                        ))}
                        {pendingFiles.map((f, idx) => (
                          <li
                            key={`${f.name}-${idx}`}
                            className="flex items-center gap-2 px-2 py-1.5 rounded border border-gray-200 bg-gray-50"
                          >
                            <FileKindIcon fileName={f.name} />
                            <span
                              className="flex-1 min-w-0 text-sm truncate"
                              title={f.name}
                            >
                              {f.name}
                            </span>
                            <span className="text-xs text-gray-400 shrink-0">
                              {formatFileSize(f.size)}
                            </span>
                            <button
                              type="button"
                              onClick={() =>
                                setPendingFiles((prev) =>
                                  prev.filter((_, i) => i !== idx),
                                )
                              }
                              className="p-1 rounded text-red-400 hover:text-red-600 shrink-0"
                              aria-label="添付を外す"
                            >
                              <CloseIcon sx={{ fontSize: 16 }} />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                    <input
                      ref={attachInputRef}
                      type="file"
                      multiple
                      className="hidden"
                      onChange={(e) => {
                        const selected = Array.from(e.target.files || []);
                        if (selected.length > 0)
                          setPendingFiles((prev) => [...prev, ...selected]);
                        e.target.value = "";
                      }}
                    />
                  </div>
                )}
              </div>

              {childTasks.length > 0 && (
                <div className="space-y-4">
                  <h4 className="font-bold">サブタスク</h4>
                  {childTasks.map((child, index) => (
                    <SubTaskForm
                      key={index}
                      task={child}
                      index={index}
                      updateTask={(i, updated) =>
                        setChildTasks((current) =>
                          current.map((c, idx) => (idx === i ? updated : c)),
                        )
                      }
                      removeTask={(i) =>
                        setChildTasks((current) =>
                          current.filter((_, idx) => idx !== i),
                        )
                      }
                      onShowMemberDialog={(idx, type) =>
                        setMemberDialogContext({
                          mode: "subtask",
                          type,
                          index: idx,
                        })
                      }
                      isInvalid={validationErrors.includes(`child-${index}`)}
                      color={color}
                      customColumns={customColumns}
                      showMemo={!task}
                    />
                  ))}
                </div>
              )}

              <div className="mt-4">
                <button
                  onClick={handleAddSubtask}
                  className="text-sm hover:underline flex items-center space-x-1"
                  style={{ color: PaletteColorHex[color] }}
                >
                  <AddIcon fontSize="small" />
                  <span>サブタスクを追加する</span>
                </button>
                <div className="relative" ref={templateMenuRef}>
                  <button
                    onClick={() =>
                      setShowSubtaskTemplates(!showSubtaskTemplates)
                    }
                    className="text-sm hover:underline flex items-center space-x-1"
                    style={{ color: PaletteColorHex[color] }}
                  >
                    <AddIcon fontSize="small" />
                    <span>サブタスクのテンプレートを使用</span>
                  </button>
                  {showSubtaskTemplates && (
                    <div className="absolute bottom-full mb-2 w-64 bg-white border rounded-lg shadow-lg z-20 max-h-60 overflow-y-auto">
                      {subtaskTemplates.length > 0 ? (
                        subtaskTemplates.map((template) => (
                          <button
                            key={template.templateId}
                            onClick={() => handleApplySubtaskTemplate(template)}
                            className="w-full text-left p-3 text-sm hover:bg-gray-100 flex flex-col gap-1"
                          >
                            <div className="flex items-center gap-2">
                              {/* テンプレートタイプに応じたアイコン */}
                              {template.templateType === "Multi" ? (
                                <ListAltIcon
                                  fontSize="small"
                                  titleAccess="マルチテンプレート"
                                />
                              ) : (
                                <TaskIcon
                                  fontSize="small"
                                  titleAccess="シングルテンプレート"
                                />
                              )}
                              <span className="truncate">
                                {template.templateName}
                              </span>
                            </div>
                            {template.templateType === "Multi" && (
                              <p className="text-xs text-gray-500">
                                {JSON.parse(template.taskData).childTasks
                                  ?.length || 0}
                                件のサブタスクが作成されます
                              </p>
                            )}
                          </button>
                        ))
                      ) : (
                        <p className="p-3 text-sm text-gray-500">
                          利用可能なサブタスクのテンプレートはありません。
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
          {view === "bulk" && (
            <>
              <div className="space-y-4">
                {taskType === "multi" && (
                  <div className="p-4 border rounded-lg space-y-4">
                    <h4 className="font-bold">親タスクの情報</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <input
                        type="text"
                        placeholder="タイトル"
                        value={formData.title}
                        onChange={(e) =>
                          handleChange({ title: e.target.value })
                        }
                        className={`md:col-span-1 p-2 border rounded ${
                          validationErrors.includes("parent-title")
                            ? "border-red-500"
                            : ""
                        }`}
                      />
                      <select
                        value={formData.priority}
                        onChange={(e) =>
                          handleChange({
                            priority: e.target.value as Task["priority"],
                          })
                        }
                        className="p-2 border rounded"
                      >
                        <option value="なし">重要度: なし</option>
                        <option value="高">高</option>
                        <option value="中">中</option>
                        <option value="低">低</option>
                      </select>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.notification}
                          onChange={(e) =>
                            handleChange({ notification: e.target.checked })
                          }
                          className="mr-2"
                        />
                        通知
                      </label>
                    </div>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <h4 className="font-bold">
                    {taskType === "multi"
                      ? "サブタスクの一括作成"
                      : "タスクの一括作成"}
                  </h4>
                  <button
                    onClick={() => setShowTitleBulk(true)}
                    className="text-sm hover:underline"
                    style={{ color: PaletteColorHex[color] }}
                  >
                    タイトルを一括入力
                  </button>
                </div>

                {/* --- 連続入力設定のUI --- */}
                <div className="p-3 border rounded-lg bg-gray-50 flex flex-col md:flex-row md:flex-wrap items-start md:items-center gap-x-4 gap-y-2 text-sm">
                  <span className="font-semibold flex-shrink-0">
                    連続入力設定:
                  </span>
                  <div className="flex flex-col md:flex-row md:flex-wrap items-start md:items-center gap-x-4 gap-y-2">
                    {!isMyList && (
                      <div className="flex gap-x-4">
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={continuousSettings.assignees}
                              onChange={(e) =>
                                setContinuousSettings((prev) => ({
                                  ...prev,
                                  assignees: e.target.checked,
                                }))
                              }
                            />
                          }
                          label={<span className="text-xs">担当者</span>}
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={continuousSettings.requesters}
                              onChange={(e) =>
                                setContinuousSettings((prev) => ({
                                  ...prev,
                                  requesters: e.target.checked,
                                }))
                              }
                            />
                          }
                          label={<span className="text-xs">依頼者</span>}
                        />
                      </div>
                    )}
                    <div className="flex gap-x-4">
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={continuousSettings.priority}
                            onChange={(e) =>
                              setContinuousSettings((prev) => ({
                                ...prev,
                                priority: e.target.checked,
                              }))
                            }
                          />
                        }
                        label={<span className="text-xs">重要度</span>}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={continuousSettings.tags}
                            onChange={(e) =>
                              setContinuousSettings((prev) => ({
                                ...prev,
                                tags: e.target.checked,
                              }))
                            }
                          />
                        }
                        label={<span className="text-xs">タグ</span>}
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  {bulkRows.map((row, index) => (
                    <div
                      key={row.id}
                      className="p-3 border rounded-lg bg-gray-50 space-y-3"
                    >
                      {/* --- 親タスク入力エリア --- */}
                      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-start">
                        <div className="md:col-span-4">
                          <div className="text-xs text-gray-500 mb-1">
                            タイトル
                          </div>
                          <input
                            type="text"
                            placeholder="タイトル"
                            value={row.title}
                            onChange={(e) => {
                              const newRows = [...bulkRows];
                              newRows[index].title = e.target.value;
                              if (e.target.value && index > 0) {
                                const prevRow = newRows[index - 1];
                                const currentRow = newRows[index];
                                if (
                                  continuousSettings.assignees &&
                                  currentRow.assignees.length === 0
                                ) {
                                  currentRow.assignees = [...prevRow.assignees];
                                }
                                if (
                                  continuousSettings.requesters &&
                                  currentRow.requesters.length === 0
                                ) {
                                  currentRow.requesters = [
                                    ...prevRow.requesters,
                                  ];
                                }
                                if (
                                  continuousSettings.priority &&
                                  currentRow.priority === "なし"
                                ) {
                                  currentRow.priority = prevRow.priority;
                                }
                                if (
                                  continuousSettings.tags &&
                                  currentRow.tags.length === 0
                                ) {
                                  currentRow.tags = [...prevRow.tags];
                                }
                              }
                              if (
                                e.target.value &&
                                index === bulkRows.length - 1
                              ) {
                                setBulkRows([
                                  ...newRows,
                                  {
                                    id: Date.now(),
                                    title: "",
                                    assignees: [],
                                    requesters: [],
                                    priority: "なし",
                                    notification: false,
                                    tags: [],
                                    subtasks: [],
                                  },
                                ]);
                              } else {
                                setBulkRows(newRows);
                              }
                            }}
                            className={`w-full p-2 border rounded ${
                              validationErrors.includes(`bulk-${index}`)
                                ? "border-red-500"
                                : ""
                            }`}
                          />
                        </div>

                        {!isMyList && (
                          <>
                            <div
                              className="md:col-span-1 cursor-pointer"
                              onClick={() =>
                                setMemberDialogContext({
                                  mode: "bulk",
                                  type: "assignees",
                                  index,
                                })
                              }
                            >
                              <div className="text-xs text-gray-500 mb-1">
                                担当 ({row.assignees?.length || 0})
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setMemberDialogContext({
                                    mode: "bulk",
                                    type: "assignees",
                                    index,
                                  });
                                }}
                                className="w-full text-left p-2 border rounded bg-white text-sm hover:bg-gray-50 truncate"
                                style={{ color: PaletteColorHex[color] }}
                              >
                                <PersonAddTwoToneIcon
                                  fontSize="small"
                                  className="mb-1 mr-1"
                                />
                              </button>
                            </div>

                            <div className="md:col-span-1">
                              <div className="text-xs text-gray-500 mb-1">
                                依頼 ({row.requesters?.length || 0})
                              </div>
                              <button
                                onClick={() =>
                                  setMemberDialogContext({
                                    mode: "bulk",
                                    type: "requesters",
                                    index,
                                  })
                                }
                                className="w-full text-left p-2 border rounded bg-white text-sm hover:bg-gray-50 truncate"
                                style={{ color: PaletteColorHex[color] }}
                              >
                                <PersonAddTwoToneIcon
                                  fontSize="small"
                                  className="mb-1 mr-1"
                                />
                              </button>
                            </div>
                          </>
                        )}

                        <div
                          className="relative md:col-span-2"
                          ref={openPriorityIndex === index ? priorityRef : null}
                        >
                          <div className="text-xs text-gray-500 mb-1">
                            重要度
                          </div>
                          <button
                            onClick={() =>
                              setOpenPriorityIndex(
                                openPriorityIndex === index ? null : index,
                              )
                            }
                            className="w-full flex items-center justify-between p-2 border rounded bg-white text-sm hover:bg-gray-50"
                          >
                            <span className="flex items-center gap-2">
                              <span
                                className={`w-3 h-3 rounded-full ${
                                  priorityConfig[row.priority].color
                                }`}
                              ></span>
                              <span>{priorityConfig[row.priority].label}</span>
                            </span>
                            <ArrowDropDownIcon fontSize="small" />
                          </button>
                          {openPriorityIndex === index && (
                            <div className="absolute top-full mt-1 w-full bg-white border rounded shadow-lg z-10">
                              {(
                                Object.keys(priorityConfig) as Array<
                                  Task["priority"]
                                >
                              ).map((p) => (
                                <button
                                  key={p}
                                  onClick={() => {
                                    const newRows = [...bulkRows];
                                    newRows[index].priority = p;
                                    setBulkRows(newRows);
                                    setOpenPriorityIndex(null);
                                  }}
                                  className="w-full text-left flex items-center gap-2 p-2 hover:bg-gray-100"
                                >
                                  <span
                                    className={`w-3 h-3 rounded-full ${priorityConfig[p].color}`}
                                  ></span>
                                  <span>{priorityConfig[p].label}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="relative md:col-span-2" ref={openTagIndex === index ? tagRef : null}>
                          <div className="text-xs text-gray-500 mb-1">
                            タグ ({row.tags.length})
                          </div>
                          <button
                            onClick={() =>
                              setOpenTagIndex(
                                openTagIndex === index ? null : index,
                              )
                            }
                            className="w-full flex items-center gap-2 p-2 border rounded bg-white text-sm hover:bg-gray-50"
                            style={{ color: PaletteColorHex[color] }}
                          >
                            <AddCircleOutlineIcon fontSize="small" />
                            追加
                          </button>
                          {openTagIndex === index && (
                            <div className="absolute top-full mt-1 w-full max-h-40 overflow-y-auto bg-white border rounded shadow-lg z-10">
                              {availableTags.map((tag) => (
                                <button
                                  key={tag.id}
                                  onClick={() =>
                                    handleBulkTagToggle(index, tag.name)
                                  }
                                  className={`w-full text-left p-2 text-sm ${
                                    row.tags.includes(tag.name)
                                      ? "bg-blue-100"
                                      : "hover:bg-gray-100"
                                  }`}
                                  style={{ color: tag.color || "inherit" }}
                                >
                                  {tag.name}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="md:col-span-2 flex items-center justify-around h-full pt-5 pb-2">
                          <label className="flex items-center text-sm cursor-pointer whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={row.notification}
                              onChange={(e) => {
                                const newRows = [...bulkRows];
                                newRows[index].notification = e.target.checked;
                                setBulkRows(newRows);
                              }}
                              className="mr-1"
                            />
                            通知
                          </label>
                          <button
                            onClick={() => {
                              if (bulkRows.length > 1) {
                                setBulkRows(
                                  bulkRows.filter((r) => r.id !== row.id),
                                );
                              }
                            }}
                            className="text-gray-400 hover:text-red-500 pb-1"
                            aria-label="削除"
                          >
                            <DeleteIcon fontSize="small" />
                          </button>
                        </div>
                      </div>

                      {row.subtasks.length > 0 && (
                        <div className="pl-8 space-y-2">
                          {row.subtasks.map((sub, subIndex) => (
                            <div
                              key={subIndex}
                              className="flex items-center gap-2 text-sm"
                            >
                              <SubdirectoryArrowRightIcon
                                fontSize="small"
                                className="text-gray-400"
                              />
                              <span className="flex-grow p-1 bg-white border rounded truncate">
                                {sub.title || "(タイトルなし)"}
                              </span>
                              <button
                                onClick={() =>
                                  setEditingSubtasksForIndex(index)
                                }
                                className="text-xs hover:underline"
                              >
                                編集
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="flex justify-end">
                        <button
                          onClick={() => setEditingSubtasksForIndex(index)}
                          className="text-xs hover:underline flex items-center gap-1"
                          style={{ color: PaletteColorHex[color] }}
                        >
                          <AddIcon fontSize="inherit" />
                          サブタスクを追加・編集
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
          {view === "template" && (
            <div className="flex flex-col p-4 border rounded-lg">
              <h4 className="font-semibold text-gray-800 mb-2">
                テンプレートから作成
              </h4>
              {isLoadingTemplates ? (
                <p>読み込み中...</p>
              ) : (
                <div className="space-y-4 overflow-y-auto">
                  {/* このルームのテンプレート */}
                  <div>
                    <h5 className="text-sm font-semibold text-gray-700 mb-2">
                      このルームのテンプレート
                    </h5>
                    <div className="space-y-2">
                      {templates.map((template) => (
                        <div
                          key={template.templateId}
                          onClick={() => applyTemplate(template)}
                          className="group w-full p-3 bg-blue-50 rounded-lg text-blue-800 hover:bg-blue-100 transition-colors cursor-pointer flex flex-col"
                        >
                          {/* 1行目: 左にアイコン+テンプレート名、右に（PCのみ）ボタン群 */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {template.templateType === "Multi" ? (
                                <ListAltIcon
                                  fontSize="small"
                                  titleAccess="マルチテンプレート"
                                />
                              ) : (
                                <TaskIcon
                                  fontSize="small"
                                  titleAccess="シングルテンプレート"
                                />
                              )}
                              <span className="truncate font-medium">
                                {template.templateName}
                              </span>
                            </div>

                            {/* PC用ボタン群（同一行の右側、md以上で表示） */}
                            <div className="hidden md:flex items-center gap-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingTemplate(template);
                                }}
                                className="p-1 rounded-full text-gray-400 hover:bg-blue-200 hover:text-blue-700"
                                title="編集"
                              >
                                <EditIcon fontSize="inherit" />
                              </button>
                              <button
                                onClick={(e) =>
                                  handleDeleteTemplate(e, template.templateId)
                                }
                                className="p-1 rounded-full text-gray-400 hover:bg-red-100 hover:text-red-600 transition-opacity"
                                aria-label="テンプレートを削除"
                              >
                                <DeleteIcon fontSize="inherit" />
                              </button>
                              {roomId !== 0 && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggleShare(e, template);
                                  }}
                                  className="flex items-center gap-1 p-1 rounded-full transition-colors hover:bg-gray-100"
                                  title={
                                    template.isShare
                                      ? "グループで共有中"
                                      : "共有していません"
                                  }
                                >
                                  {template.isShare ? (
                                    <>
                                      <GroupsIcon fontSize="inherit" />
                                      <span className="text-sm hidden md:inline">
                                        グループで共有中
                                      </span>
                                    </>
                                  ) : (
                                    <>
                                      <LockIcon fontSize="inherit" />
                                      <span className="text-sm">
                                        共有していません
                                      </span>
                                    </>
                                  )}
                                </button>
                              )}
                            </div>
                          </div>

                          {/* 2行目（モバイル専用）: 編集・削除・共有ボタン（モバイルでは2行目に出す） */}
                          <div className="flex items-center justify-start gap-3 mt-2 md:hidden">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingTemplate(template);
                              }}
                              className="flex items-center gap-1 px-2 py-1 rounded text-sm text-blue-600 hover:bg-blue-200"
                              title="編集"
                            >
                              <EditIcon fontSize="inherit" />
                              <span>編集</span>
                            </button>
                            <button
                              onClick={(e) =>
                                handleDeleteTemplate(e, template.templateId)
                              }
                              className="flex items-center gap-1 px-2 py-1 rounded text-sm text-red-600 hover:bg-red-100"
                              aria-label="テンプレートを削除"
                            >
                              <DeleteIcon fontSize="inherit" />
                              <span>削除</span>
                            </button>
                            {roomId !== 0 && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleShare(e, template);
                                }}
                                className="flex items-center gap-1 px-2 py-1 rounded text-sm hover:bg-gray-100"
                                title={
                                  template.isShare
                                    ? "グループで共有中"
                                    : "共有していません"
                                }
                              >
                                {template.isShare ? (
                                  <>
                                    <GroupsIcon fontSize="inherit" />
                                    <span>共有中</span>
                                  </>
                                ) : (
                                  <>
                                    <LockIcon fontSize="inherit" />
                                    <span>共有なし</span>
                                  </>
                                )}
                              </button>
                            )}
                          </div>

                          {/* 3行目: 件数表示（PCでもモバイルでも下の行に表示） */}
                          {template.templateType === "Multi" && (
                            <p className="text-xs text-gray-500 mt-2">
                              {JSON.parse(template.taskData).childTasks
                                ?.length || 0}
                              件のサブタスクも同時に作成されます
                            </p>
                          )}
                        </div>
                      ))}
                      {templates.length === 0 && (
                        <p className="text-sm text-gray-500">
                          このルームにテンプレートはありません。
                        </p>
                      )}
                    </div>
                  </div>

                  {/* 別ルームのテンプレート */}
                  {otherRoomTemplates.length > 0 && (
                    <div className="pt-4 border-t">
                      <h5 className="text-sm font-semibold text-gray-700 mb-2">
                        別ルームで作成したテンプレート
                      </h5>
                      <div className="space-y-2">
                        {otherRoomTemplates.map((template) => (
                          <div
                            key={template.templateId}
                            onClick={() => applyTemplate(template)}
                            className="group w-full p-3 bg-green-50 rounded-lg text-green-800 hover:bg-green-100 transition-colors cursor-pointer flex flex-col"
                          >
                            {/* 1行目: 左にアイコン+テンプレート名、右に（PCのみ）ボタン群 */}
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                {template.templateType === "Multi" ? (
                                  <ListAltIcon
                                    fontSize="small"
                                    titleAccess="マルチテンプレート"
                                  />
                                ) : (
                                  <TaskIcon
                                    fontSize="small"
                                    titleAccess="シングルテンプレート"
                                  />
                                )}
                                <span className="truncate font-medium">
                                  {template.templateName}
                                </span>
                              </div>

                              {/* PC用ボタン群（同一行の右側、md以上で表示） */}
                              <div className="hidden md:flex items-center gap-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingTemplate(template);
                                  }}
                                  className="p-1 rounded-full text-gray-400 hover:bg-green-200 hover:text-green-700"
                                  title="編集"
                                >
                                  <EditIcon fontSize="inherit" />
                                </button>
                                <button
                                  onClick={(e) =>
                                    handleDeleteTemplate(e, template.templateId)
                                  }
                                  className="p-1 rounded-full text-gray-400 hover:bg-red-100 hover:text-red-600 transition-opacity"
                                  aria-label="テンプレートを削除"
                                >
                                  <DeleteIcon fontSize="inherit" />
                                </button>
                              </div>
                            </div>

                            {/* 2行目（モバイル専用）: 編集・削除ボタン（モバイルでは2行目に出す） */}
                            <div className="flex items-center justify-start gap-3 mt-2 md:hidden">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingTemplate(template);
                                }}
                                className="flex items-center gap-1 px-2 py-1 rounded text-sm text-green-600 hover:bg-green-200"
                                title="編集"
                              >
                                <EditIcon fontSize="inherit" />
                                <span>編集</span>
                              </button>
                              <button
                                onClick={(e) =>
                                  handleDeleteTemplate(e, template.templateId)
                                }
                                className="flex items-center gap-1 px-2 py-1 rounded text-sm text-red-600 hover:bg-red-100"
                                aria-label="テンプレートを削除"
                              >
                                <DeleteIcon fontSize="inherit" />
                                <span>削除</span>
                              </button>
                            </div>

                            {/* 3行目: 件数表示（PCでもモバイルでも下の行に表示） */}
                            {template.templateType === "Multi" && (
                              <p className="text-xs text-gray-500 mt-2">
                                {JSON.parse(template.taskData).childTasks
                                  ?.length || 0}
                                件のサブタスクも同時に作成されます
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {view !== "template" && (
          <div className="mt-6 pt-4 border-t flex-shrink-0">
            {view === "single" && (
              <div className="flex justify-start mb-4">
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.notification}
                      onChange={(e) =>
                        handleChange({ notification: e.target.checked })
                      }
                    />
                  }
                  label={<span className="text-sm">作成時に通知する</span>}
                />
              </div>
            )}
            <div className="flex space-x-3">
              <button
                onClick={attemptClose}
                className="flex-1 px-4 py-2 border rounded hover:bg-gray-100"
              >
                キャンセル
              </button>
              <button
                onClick={handleSave}
                className="flex-1 px-4 py-2 text-white rounded hover:brightness-105"
                style={{ backgroundColor: PaletteColorHex[color] }}
              >
                {task ? "保存" : "作成"}
              </button>
            </div>
          </div>
        )}
        {editingTemplate && (
          <TemplateEditorPanel
            template={editingTemplate}
            onClose={() => setEditingTemplate(null)}
            roomMembers={roomMembers}
            availableTags={availableTags}
            color={color}
            honbuId={honbuId}
            roomId={roomId}
            onTemplateUpdated={handleTemplateUpdated}
          />
        )}
      </div>
    </div>
  );
}
